import * as React from 'react';
import { default as MobileSegmentFlat } from '../MobileSegmentFlat/MobileSegmentFlat';
import cn from 'classnames';
import { useTheme } from '../../../theme';
import { useMobileSegmentFlatInfo } from '../MobileSegmentFlat/hooks';
import RouteArrow from '../../../DesktopFlightRoute/components/Segment/RouteArrow/RouteArrow';
import { getDurationString } from '../../../Results/utils';
import { format } from '../../../utils';
import AircraftPopup from '../../../AircraftPopup';
import Amenities from '../../../Amenities';
import StopsInfo from '../../../DesktopFlightRoute/components/Segment/StopsInfo/StopsInfo';
import Link from '../../../Link';
import { CdnIcon } from '@shared/ui';
import ImageWithFallback from '../../../ImageWithFallback';
import { useRenderers } from '../../../renderProps';
import { useFlightIconFallbackSrc } from '../../../DesktopFlightRoute/components/Segment/FlightInfo/hooks';

interface Props extends React.ComponentProps<typeof MobileSegmentFlat> {
	className?: string;
	flightNumberClassName?: string;
}

const MobileSegmentUpgrade: React.FC<Props> = props => {
	const { MobileSegmentUpgrade: css } = useTheme('MobileSegmentInfo');

	const { firstSegment, lastSegment } = useMobileSegmentFlatInfo(props);
	const fallbackSrc = useFlightIconFallbackSrc();
	const { renderFlightInfoIcon: RenderFlightInfoIconOverride = ImageWithFallback } = useRenderers();

	const departureDate = format(firstSegment.departure.date, 'dd MMM');
	const arrivalDate = format(firstSegment.departure.date, 'dd MMM');

	return (
		<div className={cn(css.root, props.className)}>
			<div className={css.company}>
				<RenderFlightInfoIconOverride
					className={css.company__logo}
					src={firstSegment.operatingAirline?.icon}
					fallbackSrc={fallbackSrc}
					alt={firstSegment.operatingAirline?.name}
				/>

				{firstSegment.operatingAirline?.name && (
					<span className={css.company__name}>{firstSegment.operatingAirline.name}</span>
				)}
			</div>
			<div className={css.segmentInfo}>
				<span className={css.segmentInfo__date}>{departureDate}</span>
				<span className={css.segmentInfo__cityName}>{firstSegment.departure.airport.city.name}</span>
				<span className={css.segmentInfo__airportIATA}>({firstSegment.departure.airport.iata})</span>
			</div>
			<RouteArrow
				className={css.routeArrow}
				routeClassName={css.route}
				circleClassName={css.circle}
				timeInRouteClassName={css.timeInRoute}
				flightNumberClassName={cn(css.flightNumber, props.flightNumberClassName)}
				timeInRoute={getDurationString(firstSegment.duration)}
				mobile={true}
				flightNumber={`${firstSegment.operatingAirline.iata}-${firstSegment.flightNumber}`}
				stopCount={props.stopCount}
			/>
			<div className={css.segmentInfo}>
				<span className={css.segmentInfo__date}>{arrivalDate}</span>
				<span className={css.segmentInfo__cityName}>{lastSegment.arrival.airport.city.name}</span>
				<span className={css.segmentInfo__airportIATA}>({lastSegment.arrival.airport.iata})</span>
			</div>

			<div className={css.aircraftInfoAndAmenities}>
				<AircraftPopup
					id={firstSegment.aircraft.id}
					operatingIata={firstSegment.operatingAirline.iata}
					name={firstSegment.aircraft.name}
					renderButton={onCLick => (
						<Link className={css.aircraftInfo} action={onCLick}>
							{firstSegment.aircraft.name} {<CdnIcon icon={'Info/Details'} />}
						</Link>
					)}
				/>

				{firstSegment.flightInfo?.amenities?.length > 0 && (
					<Amenities className={css.amenities} amenities={firstSegment.flightInfo.amenities} />
				)}
			</div>
			{(props.segments[0].transferDuration || firstSegment.stops?.length > 0) && (
				<StopsInfo
					className={css.stopsInfo}
					stopCityName={firstSegment.arrival.airport.city.name}
					stops={firstSegment.stops}
					transferDuration={props.segments?.[0]?.transferDuration}
				/>
			)}
		</div>
	);
};

export default MobileSegmentUpgrade;
